import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useI18n, type TFunction } from "@faceheart/i18n";
import { sitesApi, employeesApi } from "../lib/api/endpoints";
import { Trash2 } from "lucide-react";
import { ImportDialog } from "../components/employees/ImportDialog";
import { AddEmployeeDialog } from "../components/employees/AddEmployeeDialog";
import { FaceBatchDialog } from "../components/employees/FaceBatchDialog";
import {
  DeleteEmployeeDialog,
  type DeleteTarget,
} from "../components/employees/DeleteEmployeeDialog";
import { ExportButton } from "../components/employees/ExportButton";

export function EmployeesListPage() {
  const { t, i18n } = useI18n("admin");
  const [page, setPage] = useState(1);
  const [siteId, setSiteId] = useState("");
  const [importing, setImporting] = useState(false);
  const [adding, setAdding] = useState(false);
  const [faceBatch, setFaceBatch] = useState(false);
  // Multi-select for bulk delete (mirrors Alerts.tsx).
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [confirmTargets, setConfirmTargets] = useState<DeleteTarget[] | null>(null);

  // Free-text search (ADR-024 "所見即可搜"). Debounced so we don't fire a query
  // per keystroke; resetting to page 1 keeps the result window honest.
  const [searchInput, setSearchInput] = useState("");
  const [q, setQ] = useState("");
  useEffect(() => {
    const id = setTimeout(() => {
      setPage(1);
      setQ(searchInput.trim());
    }, 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  // ADR-024 C2: name search is done CLIENT-SIDE over the loaded page so partial
  // matches work ("te" finds "test") — the backend name index is exact-only
  // (HMAC blind index) and can't substring-match. We load the whole org in one
  // page (org sizes are small; 100 is the page_size cap) and filter in memory.
  // site_id stays server-side (a real DB filter the client shouldn't re-derive).
  // q is intentionally NOT sent to the list query — see clientFiltered below.
  const pageSize = 100;

  const filters = {
    site_id: siteId || undefined,
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ["employees", "list", { page, ...filters }],
    queryFn: () => employeesApi.list({ page, page_size: pageSize, ...filters }),
  });

  // Client-side substring filter over name + employee_code (name is decrypted
  // plaintext on the wire). Case-insensitive, matching the backend's
  // NormalizeName(ToLower) so behaviour is consistent.
  const filteredItems = (() => {
    const items = data?.items ?? [];
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter(
      (e) =>
        e.name.toLowerCase().includes(needle) ||
        e.employee_code.toLowerCase().includes(needle),
    );
  })();

  // Selection (bulk delete). Only the currently-visible rows are selectable.
  const visibleIds = filteredItems.map((e) => e.id);
  const allSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const someSelected = visibleIds.some((id) => selectedIds.has(id));
  const toggleOne = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleAll = () =>
    setSelectedIds(allSelected ? new Set() : new Set(visibleIds));
  const clearSelection = () => setSelectedIds(new Set());
  // Build delete targets (id + name) from the current selection.
  const selectedTargets: DeleteTarget[] = filteredItems
    .filter((e) => selectedIds.has(e.id))
    .map((e) => ({ id: e.id, name: e.name }));

  // Sites power both the filter dropdown and the per-row name lookup
  // (the employee row carries site_id but not the site name). SPEC-011 Phase 2.
  const sitesQ = useQuery({
    queryKey: ["sites"],
    queryFn: sitesApi.list,
  });
  const siteNameById = new Map(
    (sitesQ.data ?? []).map((s) => [s.id, s.name]),
  );

  const dateLocale = i18n.language === "en" ? "en-US" : "zh-TW";

  return (
    <div className="p-8">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-xl font-medium">{t("employees.title")}</h1>
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={t("employees.searchPlaceholder")}
            className="bg-admin-bg3 border border-admin-border rounded px-3 py-1.5 text-sm text-admin-text1 placeholder:text-admin-text3 w-56"
          />
          <select
            value={siteId}
            onChange={(e) => {
              setPage(1);
              setSiteId(e.target.value);
            }}
            className="bg-admin-bg3 border border-admin-border rounded px-3 py-1.5 text-sm text-admin-text1"
          >
            <option value="">{t("employees.siteFilterAll")}</option>
            {(sitesQ.data ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="rounded bg-admin-cyan/90 px-3 py-1.5 text-sm font-medium text-admin-bg hover:bg-admin-cyan"
          >
            {t("employees.add.button")}
          </button>
          <button
            type="button"
            onClick={() => setImporting(true)}
            className="rounded border border-admin-border px-3 py-1.5 text-sm text-admin-text2 hover:bg-admin-bg3"
          >
            {t("employees.import.button")}
          </button>
          <button
            type="button"
            onClick={() => setFaceBatch(true)}
            className="rounded border border-admin-border px-3 py-1.5 text-sm text-admin-text2 hover:bg-admin-bg3"
          >
            {t("employees.faceBatch.button")}
          </button>
          {/* Export scopes to the SAME filters as the table (ADR-025). */}
          <ExportButton filters={filters} />
        </div>
      </div>

      {adding && (
        <AddEmployeeDialog
          sites={sitesQ.data ?? []}
          onClose={() => setAdding(false)}
        />
      )}
      {importing && <ImportDialog onClose={() => setImporting(false)} />}
      {faceBatch && <FaceBatchDialog onClose={() => setFaceBatch(false)} />}
      {confirmTargets && (
        <DeleteEmployeeDialog
          targets={confirmTargets}
          onClose={() => setConfirmTargets(null)}
          onDeleted={clearSelection}
        />
      )}

      {/* Bulk action bar — only when rows are selected. */}
      {someSelected && (
        <div className="mb-3 flex items-center gap-3 rounded border border-admin-border bg-admin-bg2 px-3 py-2 text-sm">
          <span className="text-admin-text2">
            {t("employees.delete.selected", { count: selectedTargets.length })}
          </span>
          <button
            type="button"
            onClick={() => setConfirmTargets(selectedTargets)}
            className="inline-flex items-center gap-1.5 rounded border border-admin-red/40 bg-admin-red/10 px-3 py-1 text-admin-red hover:bg-admin-red/20"
          >
            <Trash2 className="w-4 h-4" />
            {t("employees.delete.bulkButton", { count: selectedTargets.length })}
          </button>
          <button
            type="button"
            onClick={clearSelection}
            className="text-admin-text3 hover:text-admin-text1"
          >
            {t("common:actions.cancel")}
          </button>
        </div>
      )}

      {isLoading && <Skeleton />}
      {error && (
        <div className="text-admin-red text-sm">
          {t("employees.loadFailed", { message: (error as Error).message })}
        </div>
      )}

      {data && (
        <>
          <div className="bg-admin-bg2 border border-admin-border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-admin-bg3 text-admin-text2 text-xs">
                <tr>
                  <th className="px-3 py-2 w-8">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = someSelected && !allSelected;
                      }}
                      onChange={toggleAll}
                      aria-label={t("employees.delete.selectAll")}
                    />
                  </th>
                  <Th>{t("employees.tableHeaders.name")}</Th>
                  <Th>{t("employees.tableHeaders.code")}</Th>
                  <Th>{t("employees.tableHeaders.birthYear")}</Th>
                  <Th>{t("employees.tableHeaders.gender")}</Th>
                  <Th>{t("employees.tableHeaders.heightWeight")}</Th>
                  <Th>{t("employees.tableHeaders.bpGroup")}</Th>
                  <Th>{t("employees.tableHeaders.site")}</Th>
                  <Th>{t("employees.tableHeaders.consentAt")}</Th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.length === 0 ? (
                  <tr>
                    <td
                      colSpan={9}
                      className="text-center py-12 text-admin-text3"
                    >
                      {t("employees.empty")}
                    </td>
                  </tr>
                ) : (
                  filteredItems.map((e) => (
                    <tr
                      key={e.id}
                      className="border-t border-admin-border hover:bg-admin-bg3/60"
                    >
                      <td className="px-3 py-2.5">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(e.id)}
                          onChange={() => toggleOne(e.id)}
                          aria-label={t("employees.delete.selectAll")}
                        />
                      </td>
                      <td className="px-3 py-2.5">
                        <Link
                          to={`/employees/${e.id}`}
                          className="text-admin-cyan hover:underline"
                        >
                          {e.name}
                        </Link>
                      </td>
                      <td className="px-3 py-2.5 text-admin-text2">
                        {e.employee_code ?? "—"}
                      </td>
                      <td className="px-3 py-2.5 text-admin-text2">
                        {e.birth_year}
                      </td>
                      <td className="px-3 py-2.5 text-admin-text2">
                        {labelGender(t, e.gender)}
                      </td>
                      <td className="px-3 py-2.5 text-admin-text2">
                        {e.height_cm} cm / {e.weight_kg} kg
                      </td>
                      <td className="px-3 py-2.5 text-admin-text2 text-xs">
                        {labelBpGroup(t, e.bp_group)}
                      </td>
                      <td className="px-3 py-2.5 text-admin-text2 text-xs">
                        {(e.site_id && siteNameById.get(e.site_id)) || "—"}
                      </td>
                      <td className="px-3 py-2.5 text-admin-text3 text-xs">
                        {fmtDate(e.consent_at, dateLocale)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination only matters when the org exceeds one full page (>100)
              and there's no active client-side search. At ≤100 employees the
              single page holds everyone, and during search we filter that page
              in memory — paging would be meaningless. */}
          {!q.trim() && data.total > data.page_size && (
            <Pagination
              page={data.page}
              pageSize={data.page_size}
              total={data.total}
              onChange={setPage}
              t={t}
            />
          )}
        </>
      )}
    </div>
  );
}

function Skeleton() {
  return (
    <div className="bg-admin-bg2 border border-admin-border rounded-lg p-4 space-y-2">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="h-8 bg-admin-bg3/60 rounded animate-pulse" />
      ))}
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="text-left px-3 py-2 uppercase tracking-wider">{children}</th>
  );
}

function Pagination({
  page,
  pageSize,
  total,
  onChange,
  t,
}: {
  page: number;
  pageSize: number;
  total: number;
  onChange: (p: number) => void;
  t: TFunction;
}) {
  const last = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className="flex items-center justify-between mt-4 text-sm text-admin-text2">
      <span>{t("employees.pagination.summary", { total, page, last })}</span>
      <div className="flex gap-2">
        <PageBtn disabled={page <= 1} onClick={() => onChange(page - 1)}>
          {t("employees.pagination.prev")}
        </PageBtn>
        <PageBtn disabled={page >= last} onClick={() => onChange(page + 1)}>
          {t("employees.pagination.next")}
        </PageBtn>
      </div>
    </div>
  );
}

function PageBtn({
  disabled,
  onClick,
  children,
}: {
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="px-3 py-1.5 border border-admin-border rounded text-admin-text2 hover:bg-admin-bg3 disabled:opacity-40 disabled:cursor-not-allowed"
    >
      {children}
    </button>
  );
}

function labelGender(t: TFunction, g: string) {
  if (g === "male") return t("common:gender.male");
  if (g === "female") return t("common:gender.female");
  return t("common:gender.other");
}
function labelBpGroup(t: TFunction, b: string) {
  switch (b) {
    case "normal":
      return t("common:bpGroup.normal");
    case "hypertension":
      return t("common:bpGroup.hypertension");
    default:
      return b;
  }
}
function fmtDate(iso: string | undefined, locale: string) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(locale);
}
